import { useState } from "react";
import { Shield, Zap, Users, Music2, Instagram, Youtube, Twitch, Facebook } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { UniversalPlatformSearch, type PlatformId } from "@/components/UniversalPlatformSearch";

const PLATFORM_TABS: Array<{ id: PlatformId; label: string; icon: JSX.Element }> = [
  {
    id: "tiktok",
    label: "TikTok",
    icon: (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.5 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.4a6.33 6.33 0 00-.79-.05A6.34 6.34 0 003.15 15.7a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.05a8.27 8.27 0 004.76 1.5V7.12a4.83 4.83 0 01-1-.43z" />
      </svg>
    ),
  },
  { id: "youtube", label: "YouTube", icon: <Youtube className="h-4 w-4" /> },
  { id: "spotify", label: "Spotify", icon: <Music2 className="h-4 w-4" /> },
  { id: "instagram", label: "Instagram", icon: <Instagram className="h-4 w-4" /> },
  { id: "twitch", label: "Twitch", icon: <Twitch className="h-4 w-4" /> },
  {
    id: "twitter",
    label: "X",
    icon: (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  { id: "facebook", label: "Facebook", icon: <Facebook className="h-4 w-4" /> },
];

// English fallback used when a translation file does not include a
// `heroByPlatform` block for the chosen locale (hi, vi, id, tr, ru today).
const FALLBACK_COPY: Record<
  PlatformId,
  { title: string; highlight: string; subtitle: string }
> = {
  tiktok: {
    title: "Promote your",
    highlight: "TikTok content",
    subtitle: "Grow your reach on TikTok with marketing services that help your videos get discovered.",
  },
  youtube: {
    title: "Grow your",
    highlight: "YouTube channel",
    subtitle: "Promote your videos and channel with marketing services designed to expand your audience.",
  },
  spotify: {
    title: "Promote your",
    highlight: "Spotify music",
    subtitle: "Get your tracks in front of new listeners with promotion services for artists and labels.",
  },
  instagram: {
    title: "Boost your",
    highlight: "Instagram presence",
    subtitle: "Promote your profile, posts and reels with marketing services to expand your audience.",
  },
  twitch: {
    title: "Grow your",
    highlight: "Twitch stream",
    subtitle: "Promote your channel and streams with marketing services that help you reach new viewers.",
  },
  twitter: {
    title: "Promote your",
    highlight: "X profile",
    subtitle: "Expand your reach on X with promotion services for tweets, profiles and content campaigns.",
  },
  facebook: {
    title: "Promote your",
    highlight: "Facebook page",
    subtitle: "Grow your Facebook page, posts and videos with marketing services to reach a wider audience.",
  },
};

export const HeroSection = () => {
  const { t } = useI18n();
  const [platform, setPlatform] = useState<PlatformId>("tiktok");
  const copy = t.heroByPlatform?.[platform] ?? FALLBACK_COPY[platform];

  return (
    <section className="relative z-30 px-4 pt-8 pb-12 sm:pt-16 sm:pb-20 sm:px-6 lg:px-8">
      {/* Neon ambient glow */}
      <div
        className="pointer-events-none absolute -top-40 left-1/4 h-[500px] w-[500px] rounded-full opacity-20 blur-[120px]"
        style={{ background: "var(--neon-cyan)" }}
      />
      <div
        className="pointer-events-none absolute -top-20 right-1/4 h-[400px] w-[400px] rounded-full opacity-15 blur-[100px]"
        style={{ background: "var(--neon-magenta)" }}
      />

      <div className="relative mx-auto max-w-3xl text-center">
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-6xl leading-[1.1]">
          {copy.title}{" "}
          <span className="text-primary neon-text">{copy.highlight}</span>
        </h1>

        <p className="mx-auto mt-3 max-w-lg text-sm sm:text-base text-muted-foreground leading-relaxed">
          {copy.subtitle}
        </p>

        {/* Trust badges row */}
        <div className="mx-auto mt-5 flex flex-wrap items-center justify-center gap-4 text-xs font-medium text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-primary" />
            {t.hero.securePayments}
          </span>
          <span className="hidden sm:inline text-border">|</span>
          <span className="flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5 text-accent" />
            {t.hero.fastProcessing}
          </span>
          <span className="hidden sm:inline text-border">|</span>
          <span className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 text-primary" />
            {t.hero.noPassword}
          </span>
        </div>

        {/* In-place platform switcher (does NOT navigate) */}
        <div className="mx-auto mt-6 sm:mt-8 flex max-w-4xl flex-wrap items-center justify-center gap-2">
          {PLATFORM_TABS.map((tab) => {
            const isActive = tab.id === platform;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPlatform(tab.id)}
                className={[
                  "relative flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs sm:text-sm font-semibold transition-all",
                  isActive
                    ? "border-primary bg-primary/10 text-primary shadow-[0_0_16px_hsl(185_100%_50%/0.25)]"
                    : "border-border bg-secondary text-muted-foreground hover:border-primary/40 hover:text-foreground",
                ].join(" ")}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* The search swaps in-place per platform */}
        <UniversalPlatformSearch key={platform} platform={platform} ctaLabel={t.hero.cta} />
      </div>
    </section>
  );
};
